'use client';

import { type Theme, Box, Fade, Menu, SwipeableDrawer, useMediaQuery } from '@mui/material';
import { cloneElement, useId, useState } from 'react';
const bottomSheetSx = {
  '& .MuiDrawer-paper': {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    pt: 3,
    px: 2.5,
    pb: 2
  },
  '& .MuiMenuItem-root': {
    fontSize: 16,
    fontWeight: 500,
    whiteSpace: 'normal'
  },
  '& .MuiMenuItem-root .MuiSvgIcon-root': {
    width: 24
  }
};
interface ActionsMenuProps {
  /**
   * Menu items. Clicking any of them closes the menu. Pass a function to keep
   * the menu open instead: it receives `close`, and the caller decides when
   * the menu closes (e.g. once an async action has finished).
   */
  children: React.ReactNode | ((close: () => void) => React.ReactNode);
  button?: React.ReactElement<any>;
  open?: boolean;
  onClose?: () => void;
  isMobile?: boolean;
}
export function ActionsMenu({
  children,
  button,
  open: controlledOpen,
  onClose: controlledOnClose,
  isMobile = false
}: ActionsMenuProps) {
  const matches = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
    controlledOnClose?.();
  };
  const items = typeof children === 'function' ? children(handleClose) : children;
  // A render-function caller closes the menu itself via `close`.
  const handleItemsClick = typeof children === 'function' ? undefined : handleClose;
  // Unique per instance so multiple menus on one page don't share DOM ids or
  // produce duplicate `aria-controls` / `aria-labelledby` references.
  const baseId = useId();
  const menuId = `event-actions-menu-${baseId}`;
  const buttonId = `${menuId}-button`;
  return <>
      {button && cloneElement(button, {
      id: buttonId,
      'aria-haspopup': 'true',
      'aria-controls': open ? menuId : undefined,
      'aria-expanded': open ? 'true' : undefined,
      onClick: handleClick
    })}
      {isMobile || matches || !anchorEl && isControlled ? <SwipeableDrawer id={menuId} anchor="bottom" sx={bottomSheetSx} open={open} onOpen={() => {
      //
    }} onClose={handleClose}>
          <Box role="presentation" onClick={handleItemsClick}>
            {items}
          </Box>
        </SwipeableDrawer> : <Menu id={menuId} anchorEl={anchorEl} open={open} onClose={handleClose} sx={{
      '.MuiMenu-list': {
        padding: 2
      },
      '.MuiBackdrop-root': {
        backgroundColor: 'rgba(0, 0, 0, 0)'
      }
    }}
    // Menu defaults to Grow, which scales the panel in. Every other layer
    // fades, so match them.
    slots={{
      transition: Fade
    }} slotProps={{
      list: {
        'aria-labelledby': buttonId
      }
    }}>
          <Box role="presentation" onClick={handleItemsClick}>
            {items}
          </Box>
        </Menu>}
    </>;
}